<template>
    <v-container fluid grid-list-xl class="pa-3">
        <v-card>
            <v-card-title class="pb-0">
                <div>
                    <h3 class="headline mb-0 mt-0">System Settings &amp; Docs</h3>
                    <div class="grey--text body-1">
                        Change how the systems behave without a new release. Every setting has a description of what it changes,
                        and the Documentation tab explains the features in plain language. A change takes effect immediately and is recorded.
                    </div>
                </div>
            </v-card-title>

            <v-tabs v-model="tab" color="success" dark show-arrows class="mt-3">
                <v-tabs-slider color="#fff"></v-tabs-slider>
                <v-tab id="tab-settings" href="#settings">App Settings</v-tab>
                <v-tab id="tab-fund" href="#fund">Fund Settings</v-tab>
                <v-tab id="tab-queue" href="#queue">Queue Settings</v-tab>
                <v-tab id="tab-terms" href="#terms">Terms</v-tab>
                <v-tab id="tab-tables" href="#tables">Other Tables</v-tab>
                <v-tab id="tab-history" href="#history">Change History</v-tab>
                <v-tab id="tab-schema" href="#schema">
                    DB Changes
                    <v-chip v-if="schema.log.length" small color="warning" text-color="white" class="ml-2">{{ schema.log.length }}</v-chip>
                </v-tab>
                <v-tab id="tab-docs" href="#docs">Documentation</v-tab>

                <v-tab-item v-for="t in settingTabs" :key="t" :id="t" :value="t">
                    <div class="pa-3">
                        <v-layout row wrap align-center>
                            <v-flex xs12 md4>
                                <v-text-field v-model="search[t]" label="Search name, value, description or file" prepend-icon="mdi-magnify" clearable hide-details></v-text-field>
                            </v-flex>
                            <v-flex xs12 md3 v-if="t === 'settings'">
                                <v-select v-model="groupFilter" :items="groups" label="Group" clearable hide-details></v-select>
                            </v-flex>
                            <v-flex xs12 md3>
                                <v-select v-model="problemFilter[t]" :items="problemOptions" label="Show" hide-details></v-select>
                            </v-flex>
                            <v-flex xs12 md2 class="text-xs-right">
                                <v-btn v-if="t !== 'queue'" small color="success" @click="openEdit(t, null)">
                                    <v-icon left small>mdi-plus</v-icon>Add Setting
                                </v-btn>
                            </v-flex>
                        </v-layout>

                        <v-data-table
                            :headers="settingHeaders"
                            :items="filteredSettings(t)"
                            :loading="loading[t]"
                            item-key="key"
                            :rows-per-page-items="[25, 50, 100, { text: 'All', value: -1 }]"
                            :pagination.sync="pagination[t]"
                            expand
                            class="mt-3"
                        >
                            <template slot="items" slot-scope="props">
                                <tr @click="props.expanded = !props.expanded" class="pointer">
                                    <td class="text-no-wrap">
                                        <div class="font-weight-medium">{{ props.item.key }}</div>
                                        <v-chip v-for="flag in flags(props.item)" :key="flag.text" small :color="flag.color" text-color="white" class="ml-0 mr-1">{{ flag.text }}</v-chip>
                                    </td>
                                    <td class="value-cell">
                                        <span v-if="props.item.secret" class="grey--text">{{ props.item.masked || "(empty)" }}</span>
                                        <span v-else-if="!props.item.inDb" class="grey--text">(not in database)</span>
                                        <span v-else class="pre-wrap">{{ preview(props.item.value) }}</span>
                                    </td>
                                    <td>{{ props.item.group }}</td>
                                    <td>{{ props.item.description || props.item.dbDescription }}</td>
                                    <td class="text-no-wrap" @click.stop>
                                        <v-btn v-if="props.item.secret && props.item.inDb" flat icon small color="success" class="my-0" @click="reveal(t, props.item)" title="Reveal (recorded in history)">
                                            <v-icon small>mdi-eye</v-icon>
                                        </v-btn>
                                        <v-btn v-if="!props.item.readOnly && props.item.inDb" flat icon small color="success" class="my-0" @click="openEdit(t, props.item)" title="Edit">
                                            <v-icon small>mdi-pencil</v-icon>
                                        </v-btn>
                                        <v-btn v-if="!props.item.inDb && t !== 'queue'" flat icon small color="success" class="my-0" @click="openEdit(t, props.item)" title="Add this setting to the database">
                                            <v-icon small>mdi-plus</v-icon>
                                        </v-btn>
                                    </td>
                                </tr>
                            </template>
                            <template slot="expand" slot-scope="props">
                                <v-card flat class="grey lighten-4 px-4 py-3">
                                    <div v-if="props.item.notes" class="mb-2"><b>Notes:</b> {{ props.item.notes }}</div>
                                    <div v-if="props.item.dbDescription && props.item.dbDescription !== props.item.description" class="mb-2">
                                        <b>Database description:</b> {{ props.item.dbDescription }}
                                    </div>
                                    <div class="mb-2">
                                        <b>Type:</b> {{ props.item.type }}<span v-if="props.item.format"> ({{ props.item.format }})</span>
                                    </div>
                                    <div class="mb-1"><b>Read in code ({{ props.item.usedBy.length }}):</b></div>
                                    <div v-if="!props.item.usedBy.length" class="grey--text mb-2">No code references found in the scanned projects.</div>
                                    <div v-for="u in props.item.usedBy" :key="u" class="mono caption">{{ u }}</div>
                                    <div v-if="props.item.sqlUsedBy.length" class="mt-2">
                                        <b>Read by SQL:</b> <span class="mono caption">{{ props.item.sqlUsedBy.join(", ") }}</span>
                                    </div>
                                </v-card>
                            </template>
                        </v-data-table>
                    </div>
                </v-tab-item>

                <v-tab-item id="terms" value="terms">
                    <div class="pa-3">
                        <v-alert :value="true" type="info" outline class="mb-3">
                            Each row tells one SQL view which term (RegID) to show. Changing a row changes what that view returns everywhere: web, phone lines, and reports.
                            Select one or more rows and choose Change term. The new term is confirmed before anything is saved.
                            Term-type app settings (like CurrentStudentRegID) are listed under "Roll forward".
                        </v-alert>

                        <v-expansion-panel class="mb-3">
                            <v-expansion-panel-content>
                                <div slot="header" class="font-weight-medium">Roll forward to a new term (yearly rollover)</div>
                                <v-card class="pa-3">
                                    <v-layout row wrap align-center>
                                        <v-flex xs12 md3>
                                            <v-select v-model="rollover.from" :items="termValues" label="Change rows that are now"></v-select>
                                        </v-flex>
                                        <v-flex xs12 md3>
                                            <v-text-field v-model="rollover.to" label="To" placeholder="RG2028" @input="rollover.to = (rollover.to || '').toUpperCase()"></v-text-field>
                                        </v-flex>
                                        <v-flex xs12 md6>
                                            <v-btn small color="success" :disabled="!rolloverSelected.length || !validTerm(rollover.to) || saving" @click="applyRollover">
                                                Update {{ rolloverSelected.length }} selected
                                            </v-btn>
                                        </v-flex>
                                    </v-layout>
                                    <div v-if="rollover.from" class="mt-2">
                                        <div v-if="!rolloverCandidates.length" class="grey--text">Nothing currently set to {{ rollover.from }}.</div>
                                        <v-layout row wrap>
                                            <v-flex xs12 md6 v-for="c in rolloverCandidates" :key="c.uid" class="py-0">
                                                <v-checkbox v-model="c.selected" color="success" hide-details class="mt-0">
                                                    <span slot="label">
                                                        <b>{{ c.label }}</b>
                                                        <span class="grey--text caption ml-1">{{ c.kind === "term" ? "TermSettings" : "App setting" }}</span>
                                                    </span>
                                                </v-checkbox>
                                            </v-flex>
                                        </v-layout>
                                    </div>
                                </v-card>
                            </v-expansion-panel-content>
                        </v-expansion-panel>

                        <v-layout row align-center class="mb-2">
                            <v-text-field v-model="search.terms" label="Search" prepend-icon="mdi-magnify" clearable hide-details style="max-width: 400px"></v-text-field>
                            <v-spacer></v-spacer>
                            <v-btn small color="success" class="ml-3" :disabled="!termSelected.length" @click="openTermChange(termSelected)">
                                Change term<span v-if="termSelected.length"> ({{ termSelected.length }})</span>
                            </v-btn>
                        </v-layout>
                        <v-data-table v-model="termSelected" :headers="termHeaders" :items="filteredTerms" :loading="loading.terms" item-key="id" select-all :rows-per-page-items="[50, { text: 'All', value: -1 }]" expand>
                            <template slot="items" slot-scope="props">
                                <tr @click="props.expanded = !props.expanded" class="pointer">
                                    <td @click.stop>
                                        <v-checkbox v-model="props.selected" color="success" hide-details class="my-0"></v-checkbox>
                                    </td>
                                    <td class="font-weight-medium text-no-wrap">
                                        {{ props.item.source }}
                                        <v-chip v-if="!props.item.objectType" small color="error" text-color="white">object not found</v-chip>
                                    </td>
                                    <td @click.stop class="text-no-wrap">
                                        <a href="#" class="term-link" @click.prevent="openTermChange([props.item])">{{ props.item.term }}</a>
                                    </td>
                                    <td>{{ props.item.description }}</td>
                                    <td class="text-no-wrap">{{ props.item.readBy.length }}</td>
                                    <td class="text-no-wrap">{{ formatDate(props.item.modified) }}</td>
                                </tr>
                            </template>
                            <template slot="expand" slot-scope="props">
                                <v-card flat class="grey lighten-4 px-4 py-3">
                                    <b>SQL objects that look up this row:</b>
                                    <span class="mono caption">{{ props.item.readBy.join(", ") || "none found" }}</span>
                                </v-card>
                            </template>
                        </v-data-table>
                    </div>
                </v-tab-item>

                <v-tab-item id="tables" value="tables">
                    <div class="pa-3">
                        <v-alert :value="true" type="info" outline class="mb-3">
                            Other settings tables. "System" tables hold configuration and can be viewed here (read-only; secret-looking columns are masked).
                            "Per-record" tables hold settings for each account, student, teacher or user and are edited on their own pages.
                        </v-alert>
                        <v-data-table :headers="tableHeaders" :items="featureTables" :loading="loading.tables" hide-actions>
                            <template slot="items" slot-scope="props">
                                <td class="font-weight-medium text-no-wrap">{{ props.item.name }}</td>
                                <td>
                                    <v-chip small :color="props.item.scope === 'system' ? 'success' : 'grey'" text-color="white">{{ props.item.scope }}</v-chip>
                                </td>
                                <td>{{ props.item.rows === null ? "missing" : props.item.rows }}</td>
                                <td>{{ props.item.description }}</td>
                                <td>{{ props.item.usedBy.join(", ") }}</td>
                                <td class="text-no-wrap">
                                    <v-btn v-if="props.item.scope === 'system' && props.item.rows !== null" small flat color="success" class="my-0" @click="viewTable(props.item)">View</v-btn>
                                    <v-btn v-if="props.item.editPage" small flat color="success" class="my-0" :to="props.item.editPage">Open page</v-btn>
                                </td>
                            </template>
                        </v-data-table>
                    </div>
                </v-tab-item>

                <v-tab-item id="history" value="history">
                    <div class="pa-3">
                        <v-text-field v-model="search.history" label="Search" prepend-icon="mdi-magnify" clearable hide-details class="mb-2" style="max-width: 400px"></v-text-field>
                        <v-data-table :headers="historyHeaders" :items="history" :search="search.history" :loading="loading.history" :rows-per-page-items="[50, 100, { text: 'All', value: -1 }]">
                            <template slot="items" slot-scope="props">
                                <td class="text-no-wrap">{{ formatDate(props.item.dateTime) }}</td>
                                <td class="text-no-wrap">{{ props.item.userName }}</td>
                                <td class="text-no-wrap">{{ props.item.tableName }}</td>
                                <td class="text-no-wrap font-weight-medium">{{ props.item.keyValues }}</td>
                                <td>{{ props.item.action }}</td>
                                <td class="pre-wrap">{{ describeChanges(props.item.changes) }}</td>
                            </template>
                        </v-data-table>
                    </div>
                </v-tab-item>

                <v-tab-item id="schema" value="schema">
                    <div class="pa-3">
                        <v-alert :value="!schema.installed" type="warning" outline class="mb-3">
                            The SQL change log is not installed yet. Run <span class="mono">Scripts/CreateSchemaChangeLog.sql</span> against the Payments database
                            to record every table, view, procedure, function and trigger change. Until then this tab only shows recently modified objects.
                        </v-alert>

                        <template v-if="schema.installed">
                            <div class="caption grey--text mb-2">
                                These SQL changes are not in the docs yet. The next docs update in the repo picks them up and clears them.
                                Nothing to mark on this page.
                            </div>
                            <v-data-table :headers="schemaHeaders" :items="schema.log" item-key="id" :loading="loading.schema" :rows-per-page-items="[50, 100, { text: 'All', value: -1 }]">
                                <template slot="items" slot-scope="props">
                                    <td class="text-no-wrap">{{ formatDate(props.item.eventDate) }}</td>
                                    <td class="text-no-wrap">{{ props.item.eventType }}</td>
                                    <td class="text-no-wrap font-weight-medium">{{ props.item.schemaName }}.{{ props.item.objectName }}</td>
                                    <td class="text-no-wrap">{{ props.item.loginName }}</td>
                                    <td>{{ props.item.appName }}</td>
                                    <td>
                                        <v-btn small flat color="success" class="my-0" @click="viewCommand(props.item)">SQL</v-btn>
                                    </td>
                                </template>
                            </v-data-table>
                        </template>

                        <h4 class="title mt-4 mb-2">Objects modified in the last {{ schema.days }} days</h4>
                        <v-data-table :headers="recentHeaders" :items="schema.recent" :loading="loading.schema" :rows-per-page-items="[25, 50, { text: 'All', value: -1 }]">
                            <template slot="items" slot-scope="props">
                                <td class="text-no-wrap font-weight-medium">{{ props.item.schemaName }}.{{ props.item.objectName }}</td>
                                <td>{{ props.item.objectType }}</td>
                                <td class="text-no-wrap">{{ formatDate(props.item.modified) }}</td>
                                <td class="text-no-wrap">{{ formatDate(props.item.created) }}</td>
                            </template>
                        </v-data-table>
                    </div>
                </v-tab-item>

                <v-tab-item id="docs" value="docs">
                    <div class="docs-shell">
                        <div class="docs-nav">
                            <div class="docs-nav-group">Guide</div>
                            <div
                                v-for="d in guideDocs"
                                :key="d.path"
                                class="docs-nav-item"
                                :class="{ 'docs-nav-item--active': d.path === doc.path }"
                                @click="openDoc(d.path)"
                            >{{ d.title }}</div>

                            <template v-if="systemDocs.length">
                                <div class="docs-nav-group mt-3">Technical reference</div>
                                <div
                                    v-for="d in systemDocs"
                                    :key="d.path"
                                    class="docs-nav-item"
                                    :class="{ 'docs-nav-item--active': d.path === doc.path }"
                                    @click="openDoc(d.path)"
                                >{{ d.title }}</div>
                            </template>
                            <div v-if="!docs.length && !loading.docs" class="grey--text pa-3">No documentation found.</div>
                        </div>

                        <div class="docs-reader" ref="docReader">
                            <div class="markdown-body" v-html="docHtml" @click="docLinkClicked"></div>
                        </div>

                        <div class="docs-chat">
                            <div class="docs-chat-head">
                                Ask
                                <div class="docs-chat-sub">Kept for your login</div>
                            </div>
                            <div class="ask-log" ref="askLog">
                                <div v-if="!ask.messages.length" class="ask-empty">
                                    Ask how a feature works, which setting controls it, or what happens when something fails.
                                    <div class="mt-3">
                                        <div
                                            v-for="prompt in askPrompts"
                                            :key="prompt"
                                            class="ask-chip"
                                            @click="ask.text = prompt; askQuestion()"
                                        >{{ prompt }}</div>
                                    </div>
                                </div>
                                <div v-for="(message, index) in ask.messages" :key="index" class="ask-turn">
                                    <div class="ask-bubble ask-bubble--user">
                                        {{ message.question }}
                                        <div v-if="message.askedOn" class="ask-when">{{ formatDate(message.askedOn) }}</div>
                                    </div>
                                    <div class="ask-bubble ask-bubble--assistant markdown-body" v-html="message.html"></div>
                                </div>
                            </div>
                            <div class="docs-chat-input">
                                <v-textarea
                                    v-model="ask.text"
                                    placeholder="Ask a question…"
                                    append-icon="mdi-send"
                                    solo
                                    flat
                                    hide-details
                                    auto-grow
                                    rows="1"
                                    :loading="ask.busy"
                                    @keydown.native="onAskKeydown"
                                    @click:append="askQuestion"
                                ></v-textarea>
                            </div>
                        </div>
                    </div>
                </v-tab-item>
            </v-tabs>
        </v-card>

        <v-dialog v-model="termDialog.open" max-width="720" persistent>
            <v-card>
                <v-toolbar dark color="success" dense>
                    <v-toolbar-title>Change term</v-toolbar-title>
                    <v-spacer></v-spacer>
                    <v-btn icon dark class="my-0" @click="termDialog.open = false">
                        <v-icon class="white--text">mdi-close</v-icon>
                    </v-btn>
                </v-toolbar>
                <v-card-text class="pt-3">
                    <p class="mb-3">
                        <template v-if="termDialog.items.length === 1">
                            <b>{{ termDialog.items[0].source }}</b>
                            <span v-if="termDialog.items[0].description"> — {{ termDialog.items[0].description }}</span>
                        </template>
                        <template v-else>
                            {{ termDialog.items.length }} views will be set to the same new term.
                        </template>
                    </p>
                    <p class="mb-3">
                        Saving changes the term that view reads. The next time it runs, screens, reports, and phone lines that use it show the new year. Nothing is copied from the old year, and the old year's rows stay where they are.
                    </p>
                    <div class="term-change-list mb-3">
                        <div v-for="item in termDialog.items" :key="item.id" class="term-change-row">
                            <div class="font-weight-medium">
                                {{ item.source }}
                                <v-chip v-if="!item.objectType" small color="error" text-color="white">object not found</v-chip>
                            </div>
                            <div v-if="termDialog.items.length > 1 && item.description" class="grey--text">{{ item.description }}</div>
                            <div class="caption">
                                Now <b>{{ item.term }}</b>.
                                <span v-if="termWillChange(item)">After you save, this becomes <b>{{ (termDialog.to || '').trim() }}</b>.</span>
                                <span v-else>That is already this term, so this row will be left as it is.</span>
                            </div>
                            <div class="caption grey--text">
                                Used by: {{ item.readBy.length ? item.readBy.join(", ") : "no SQL object was found that looks up this row" }}
                            </div>
                        </div>
                    </div>
                    <v-text-field
                        v-model="termDialog.to"
                        label="New term"
                        placeholder="RG2028"
                        :rules="[termRule]"
                        @input="termDialog.to = (termDialog.to || '').toUpperCase()"
                    ></v-text-field>
                </v-card-text>
                <v-card-actions class="px-3 pb-3">
                    <v-spacer></v-spacer>
                    <v-btn flat @click="termDialog.open = false">Cancel</v-btn>
                    <v-btn color="success" :disabled="!canSaveTermDialog" :loading="saving" @click="saveTermDialog">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="edit.open" max-width="760" persistent>
            <v-card>
                <v-toolbar dark color="success" dense>
                    <v-toolbar-title>{{ edit.isNew ? "Add setting " + (edit.key || "") : "Edit " + edit.key }}</v-toolbar-title>
                    <v-spacer></v-spacer>
                    <v-btn icon dark class="my-0" @click="edit.open = false">
                        <v-icon class="white--text">mdi-close</v-icon>
                    </v-btn>
                </v-toolbar>
                <v-card-text>
                    <v-alert :value="edit.phoneWarning" type="warning" outline class="mb-3">
                        This setting is read by a phone line (IVR). Callers get the new value on their next call.
                    </v-alert>
                    <div v-if="edit.item && edit.item.description" class="mb-3 grey--text text--darken-1">{{ edit.item.description }}</div>

                    <v-text-field v-if="edit.isNew" v-model="edit.key" label="Name" :rules="[rules.required]"></v-text-field>

                    <template v-if="edit.secret && !edit.isNew">
                        <v-checkbox v-model="edit.changeSecret" color="success" label="Replace the secret value" hide-details class="mb-2"></v-checkbox>
                        <v-text-field v-if="edit.changeSecret" v-model="edit.value" label="New value" type="password" autocomplete="new-password"></v-text-field>
                    </template>
                    <v-select v-else-if="edit.type === 'bool'" v-model="edit.value" :items="['true', 'false']" label="Value"></v-select>
                    <v-textarea v-else-if="edit.type === 'multiline'" v-model="edit.value" label="Value" auto-grow rows="4"></v-textarea>
                    <v-text-field
                        v-else
                        v-model="edit.value"
                        label="Value"
                        :hint="valueHint"
                        persistent-hint
                    ></v-text-field>

                    <v-combobox v-if="edit.table === 'settings'" v-model="edit.group" :items="groups" label="Group" class="mt-3"></v-combobox>
                    <v-textarea v-model="edit.description" label="Database description" rows="2" auto-grow class="mt-2"
                        hint="Stored in the database. Full documentation lives in docs/settings/settings-catalog.json." persistent-hint></v-textarea>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn flat @click="edit.open = false">Cancel</v-btn>
                    <v-btn color="success" :loading="saving" @click="saveEdit">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="revealDialog.open" max-width="640">
            <v-card>
                <v-toolbar dark color="success" dense>
                    <v-toolbar-title>{{ revealDialog.key }}</v-toolbar-title>
                    <v-spacer></v-spacer>
                    <v-btn icon dark class="my-0" @click="closeReveal">
                        <v-icon class="white--text">mdi-close</v-icon>
                    </v-btn>
                </v-toolbar>
                <v-card-text>
                    <div class="caption grey--text mb-2">Viewing this value was recorded in the change history.</div>
                    <div class="mono pre-wrap break-all">{{ revealDialog.value }}</div>
                </v-card-text>
            </v-card>
        </v-dialog>

        <v-dialog v-model="tableDialog.open" max-width="1400">
            <v-card>
                <v-toolbar dark color="success" dense>
                    <v-toolbar-title>{{ tableDialog.name }}</v-toolbar-title>
                    <v-spacer></v-spacer>
                    <v-btn icon dark class="my-0" @click="tableDialog.open = false">
                        <v-icon class="white--text">mdi-close</v-icon>
                    </v-btn>
                </v-toolbar>
                <v-card-text class="table-scroll">
                    <table class="simple-table">
                        <thead>
                            <tr><th v-for="c in tableDialog.columns" :key="c">{{ c }}</th></tr>
                        </thead>
                        <tbody>
                            <tr v-for="(row, i) in tableDialog.rows" :key="i">
                                <td v-for="c in tableDialog.columns" :key="c">{{ preview(row[c] === null || row[c] === undefined ? "" : String(row[c])) }}</td>
                            </tr>
                        </tbody>
                    </table>
                </v-card-text>
            </v-card>
        </v-dialog>

        <v-dialog v-model="commandDialog.open" max-width="1100">
            <v-card>
                <v-toolbar dark color="success" dense>
                    <v-toolbar-title>{{ commandDialog.title }}</v-toolbar-title>
                    <v-spacer></v-spacer>
                    <v-btn icon dark class="my-0" @click="commandDialog.open = false">
                        <v-icon class="white--text">mdi-close</v-icon>
                    </v-btn>
                </v-toolbar>
                <v-card-text>
                    <div v-if="commandDialog.loading" class="grey--text">Loading the SQL…</div>
                    <pre v-else class="mono sql-box">{{ commandDialog.command || "No SQL was recorded for this change." }}</pre>
                </v-card-text>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import moment from "moment";
import { marked } from "marked";

const TERM_PATTERN = /^[A-Z]{2,4}\d{4}$/;
const ASK_STORAGE_KEY = "uta.docsAsk";
const TABLE_KEYS = { settings: "dbo", fund: "fund", queue: "queue" };
const PHONE_PROJECTS = ["UTA Plivo", "ivr-system"];

export default {
    data: () => ({
        tab: "settings",
        settingTabs: ["settings", "fund", "queue"],
        settings: { settings: [], fund: [], queue: [] },
        loading: { settings: false, fund: false, queue: false, terms: false, tables: false, history: false, schema: false, docs: false },
        search: { settings: "", fund: "", queue: "", terms: "", history: "" },
        problemFilter: { settings: "all", fund: "all", queue: "all" },
        pagination: {
            settings: { rowsPerPage: 50 },
            fund: { rowsPerPage: 50 },
            queue: { rowsPerPage: 50 },
        },
        groupFilter: null,
        problemOptions: [
            { text: "All settings", value: "all" },
            { text: "Needs attention (any flag)", value: "problems" },
            { text: "Undocumented", value: "undocumented" },
            { text: "Not read anywhere", value: "unused" },
            { text: "Secrets", value: "secret" },
            { text: "Read by phone lines (IVR)", value: "ivr" },
        ],
        settingHeaders: [
            { text: "Name", value: "key" },
            { text: "Value", value: "value", sortable: false },
            { text: "Group", value: "group" },
            { text: "Description", value: "description", sortable: false },
            { text: "", value: "actions", sortable: false },
        ],
        terms: [],
        termSelected: [],
        termDialog: { open: false, items: [], to: "" },
        termHeaders: [
            { text: "View / source", value: "source" },
            { text: "Term", value: "term" },
            { text: "Description", value: "description", sortable: false },
            { text: "Read by", value: "readBy", sortable: false },
            { text: "View last changed", value: "modified" },
        ],
        rollover: { from: null, to: "" },
        rolloverCandidates: [],
        featureTables: [],
        tableHeaders: [
            { text: "Table", value: "name" },
            { text: "Kind", value: "scope" },
            { text: "Rows", value: "rows" },
            { text: "Description", value: "description", sortable: false },
            { text: "Used by", value: "usedBy", sortable: false },
            { text: "", value: "actions", sortable: false },
        ],
        history: [],
        historyHeaders: [
            { text: "When", value: "dateTime" },
            { text: "Who", value: "userName" },
            { text: "Table", value: "tableName" },
            { text: "Setting", value: "keyValues" },
            { text: "Action", value: "action" },
            { text: "Change", value: "changes", sortable: false },
        ],
        schema: { installed: true, log: [], recent: [], days: 60 },
        schemaHeaders: [
            { text: "When", value: "eventDate" },
            { text: "Event", value: "eventType" },
            { text: "Object", value: "objectName" },
            { text: "Login", value: "loginName" },
            { text: "App", value: "appName" },
            { text: "", value: "sql", sortable: false },
        ],
        recentHeaders: [
            { text: "Object", value: "objectName" },
            { text: "Type", value: "objectType" },
            { text: "Modified", value: "modified" },
            { text: "Created", value: "created" },
        ],
        docs: [],
        doc: { path: null, content: "" },
        ask: { text: "", busy: false, messages: [] },
        askPrompts: [
            "Which pages can send a broadcast, and what happens when one fails?",
            "How does a bus change pick a route for a student?",
            "What does changing the school year actually change?",
            "What are the options on the tuition phone line?",
        ],
        loaded: {},
        saving: false,
        edit: { open: false },
        revealDialog: { open: false, key: "", value: "" },
        tableDialog: { open: false, name: "", columns: [], rows: [] },
        commandDialog: { open: false, title: "", command: "", loading: false },
        rules: {
            required: (value) => !!value || "Required",
        },
    }),
    created() {
        const tab = this.$route.params.tab;
        if (tab) this.tab = tab;
        this.loadTab(this.tab);
        this.loadSchema();
    },
    watch: {
        tab(value) {
            if (this.$route.params.tab !== value) {
                this.$router.replace({ name: "SystemSettings", params: { tab: value } });
            }
            this.loadTab(value);
        },
        "rollover.from"() {
            this.buildRolloverCandidates();
        },
    },
    computed: {
        groups() {
            return [...new Set(this.settings.settings.map((s) => s.group).filter((g) => g))].sort();
        },
        filteredTerms() {
            const q = (this.search.terms || "").toLowerCase();
            if (!q) return this.terms;
            return this.terms.filter((t) =>
                [t.source, t.term, t.description, t.readBy.join(" ")].some((v) => (v || "").toLowerCase().includes(q)),
            );
        },
        canSaveTermDialog() {
            const to = (this.termDialog.to || "").trim();
            return this.validTerm(to) && this.termDialog.items.some((item) => item.term !== to);
        },
        termValues() {
            const values = this.terms
                .map((t) => t.term)
                .concat(this.settings.settings.filter((s) => s.type === "regid").map((s) => (s.value || "").trim()));
            return [...new Set(values.filter((v) => TERM_PATTERN.test(v)))].sort();
        },
        rolloverSelected() {
            return this.rolloverCandidates.filter((c) => c.selected);
        },
        docHtml() {
            return this.doc.content ? marked.parse(this.doc.content) : "";
        },
        guideDocs() {
            return this.docs.filter((d) => d.path.startsWith("guide/"));
        },
        systemDocs() {
            return this.docs.filter((d) => !d.path.startsWith("guide/"));
        },
        valueHint() {
            const e = this.edit;
            if (e.type === "regid") return "Term, e.g. RG2027";
            if (e.type === "date" || e.type === "datetime") return "Saved as " + (e.format || "entered");
            if (e.type === "emailList") return "Separate addresses with commas";
            if (e.type === "int" || e.type === "number") return "Number";
            return "";
        },
    },
    methods: {
        async loadTab(tab) {
            if (this.loaded[tab]) return;
            this.loaded[tab] = true;

            if (this.settingTabs.includes(tab)) return this.loadSettings(tab);
            if (tab === "terms") {
                if (!this.loaded.settings) {
                    this.loaded.settings = true;
                    await this.loadSettings("settings");
                }
                return this.loadTerms();
            }
            if (tab === "tables") return this.loadTables();
            if (tab === "history") return this.loadHistory();
            if (tab === "docs") return this.loadDocs();
        },
        async loadSettings(tab) {
            this.loading[tab] = true;
            try {
                this.settings[tab] = await this.$http.get("api/systemSettings/settings", { params: { table: TABLE_KEYS[tab] } });
            } finally {
                this.loading[tab] = false;
            }
        },
        async loadTerms() {
            this.loading.terms = true;
            try {
                const rows = await this.$http.get("api/systemSettings/termSettings");
                this.terms = rows;
                this.termSelected = [];
                this.buildRolloverCandidates();
            } finally {
                this.loading.terms = false;
            }
        },
        async loadTables() {
            this.loading.tables = true;
            try {
                this.featureTables = await this.$http.get("api/systemSettings/featureTables");
            } finally {
                this.loading.tables = false;
            }
        },
        async loadHistory() {
            this.loading.history = true;
            try {
                const rows = await this.$http.get("api/systemSettings/history", { params: { take: 500 } });
                this.history = rows.map((r) => this.camel(r));
            } finally {
                this.loading.history = false;
            }
        },
        async loadSchema() {
            this.loading.schema = true;
            try {
                const result = await this.$http.get("api/systemSettings/schemaChanges", {
                    params: { days: this.schema.days },
                });
                this.schema.installed = result.installed;
                this.schema.log = result.log.map((r) => this.camel(r));
                this.schema.recent = result.recent.map((r) => this.camel(r));
            } finally {
                this.loading.schema = false;
            }
        },
        async loadDocs() {
            this.loading.docs = true;
            try {
                this.docs = await this.$http.get("api/systemSettings/docs");
                if (this.docs.length && !this.doc.path) await this.openDoc(this.docs[0].path);
                await this.loadAsk();
            } finally {
                this.loading.docs = false;
            }
        },
        readLocalAsk() {
            try {
                const rows = JSON.parse(localStorage.getItem(ASK_STORAGE_KEY) || "[]");
                return Array.isArray(rows) ? rows : [];
            } catch (e) {
                return [];
            }
        },
        writeLocalAsk() {
            const rows = this.ask.messages.map((m) => ({ question: m.question, answer: m.answer, askedOn: m.askedOn || null }));
            localStorage.setItem(ASK_STORAGE_KEY, JSON.stringify(rows));
        },
        mapAskRows(rows) {
            return (rows || []).map((r) => ({
                question: r.question,
                answer: r.answer,
                html: marked.parse(r.answer || ""),
                askedOn: r.askedOn || null,
            }));
        },
        async loadAsk() {
            try {
                const result = await this.$http.get("api/systemSettings/askHistory");
                if (result && result.stored) {
                    this.ask.messages = this.mapAskRows(result.messages);
                    this.writeLocalAsk();
                    return;
                }
            } catch (e) {
                // The log table is not available yet. Use the copy kept in this browser.
            }
            this.ask.messages = this.mapAskRows(this.readLocalAsk());
        },
        onAskKeydown(event) {
            if (event.key !== "Enter" || event.shiftKey || event.ctrlKey || event.altKey || event.metaKey) return;
            event.preventDefault();
            this.askQuestion();
        },
        async askQuestion() {
            const question = (this.ask.text || "").trim();
            if (question.length < 3 || this.ask.busy) return;

            this.ask.busy = true;
            const history = this.ask.messages.slice(-4).reduce((turns, message) => {
                turns.push({ role: "user", text: message.question });
                turns.push({ role: "assistant", text: message.answer });
                return turns;
            }, []);
            try {
                const result = await this.$http.post("api/systemSettings/ask", { question, history });
                this.ask.messages.push({
                    question,
                    answer: result.answer,
                    html: marked.parse(result.answer),
                    askedOn: moment().format("YYYY-MM-DD HH:mm:ss"),
                });
                this.writeLocalAsk();
                this.ask.text = "";
                this.$nextTick(() => {
                    const log = this.$refs.askLog;
                    if (log) log.scrollTop = log.scrollHeight;
                });
            } finally {
                this.ask.busy = false;
            }
        },
        async openDoc(path) {
            const result = await this.$http.get("api/systemSettings/doc", { params: { path } });
            this.doc = result;
            this.$nextTick(() => {
                const reader = this.$refs.docReader;
                if (reader) reader.scrollTop = 0;
            });
        },
        docLinkClicked(e) {
            const link = e.target.closest("a");
            if (!link) return;
            const href = link.getAttribute("href") || "";
            if (/^[a-z]+:/i.test(href) || href.startsWith("#") || !href.toLowerCase().split("#")[0].endsWith(".md")) {
                if (/^https?:/i.test(href)) link.setAttribute("target", "_blank");
                return;
            }
            e.preventDefault();
            const base = this.doc.path.split("/").slice(0, -1);
            href.split("#")[0].split("/").forEach((part) => {
                if (part === "..") base.pop();
                else if (part && part !== ".") base.push(part);
            });
            this.openDoc(base.join("/"));
        },
        camel(row) {
            const out = {};
            Object.keys(row).forEach((k) => {
                const key = k === k.toUpperCase() ? k.toLowerCase() : k.charAt(0).toLowerCase() + k.slice(1);
                out[key] = row[k];
            });
            return out;
        },
        filteredSettings(tab) {
            const q = (this.search[tab] || "").toLowerCase();
            const filter = this.problemFilter[tab];
            return this.settings[tab].filter((s) => {
                if (tab === "settings" && this.groupFilter && s.group !== this.groupFilter) return false;
                if (filter === "undocumented" && s.documented) return false;
                if (filter === "unused" && !this.isUnused(s)) return false;
                if (filter === "secret" && !s.secret) return false;
                if (filter === "ivr" && !this.readByPhone(s)) return false;
                if (filter === "problems" && !this.flags(s).some((f) => f.problem)) return false;
                if (!q) return true;
                return [s.key, s.secret ? "" : s.value, s.group, s.description, s.dbDescription, s.usedBy.join(" "), s.sqlUsedBy.join(" ")]
                    .some((v) => (v || "").toLowerCase().includes(q));
            });
        },
        isUnused(s) {
            return s.inDb && !s.usedBy.length && !s.sqlUsedBy.length;
        },
        readByPhone(s) {
            return s.usedBy.some((u) => PHONE_PROJECTS.some((p) => u.startsWith(p + ":")));
        },
        flags(s) {
            const flags = [];
            if (s.secret) flags.push({ text: "secret", color: "deep-purple" });
            if (s.readOnly) flags.push({ text: "system managed", color: "blue-grey" });
            if (this.readByPhone(s)) flags.push({ text: "IVR", color: "orange darken-2" });
            if (!s.inDb) flags.push({ text: "not in DB", color: "error", problem: true });
            if (!s.documented) flags.push({ text: "undocumented", color: "warning", problem: true });
            if (this.isUnused(s)) flags.push({ text: "not read anywhere", color: "grey", problem: true });
            if (s.hasWhitespaceIssue) flags.push({ text: "extra spaces/line breaks", color: "error", problem: true });
            return flags;
        },
        preview(value) {
            if (value === null || value === undefined) return "";
            return value.length > 160 ? value.substring(0, 160) + "…" : value;
        },
        formatDate(value) {
            return value ? moment(value).format("MM/DD/YYYY h:mm A") : "";
        },
        validTerm(value) {
            return TERM_PATTERN.test((value || "").trim());
        },
        describeChanges(changes) {
            if (!changes) return "";
            try {
                const parsed = JSON.parse(changes);
                return Object.keys(parsed)
                    .map((k) => {
                        const c = parsed[k];
                        const show = (v) => (v === null || v === undefined || v === "" ? "(empty)" : this.preview(String(v)));
                        return `${c.Field || k}: ${show(c.Was)} → ${show(c.Now)}`;
                    })
                    .join("\n");
            } catch (e) {
                return changes;
            }
        },
        openEdit(tab, item) {
            this.edit = {
                open: true,
                table: tab,
                isNew: !item || !item.inDb,
                item,
                key: item ? item.key : "",
                value: item && !item.secret ? item.value : "",
                group: item ? item.group : null,
                description: item ? item.dbDescription || (item.inDb ? "" : item.description) || "" : "",
                type: item ? item.type : "text",
                format: item ? item.format : null,
                secret: item ? item.secret : false,
                changeSecret: false,
                phoneWarning: item ? this.readByPhone(item) : false,
            };
        },
        async saveEdit() {
            const e = this.edit;
            if (e.isNew && !(e.key || "").trim()) return;

            const body = {
                table: TABLE_KEYS[e.table],
                key: e.key,
                isNew: e.isNew,
                value: e.secret && !e.isNew && !e.changeSecret ? null : e.value || "",
                group: e.table === "settings" ? e.group || "" : null,
                description: e.description || "",
            };

            this.saving = true;
            try {
                await this.$http.post("api/systemSettings/save", body);
                this.$notify({ group: "notification", type: "success", text: `${e.key} saved` });
                this.edit.open = false;
                await this.loadSettings(e.table);
                this.loaded.history = false;
            } finally {
                this.saving = false;
            }
        },
        async reveal(tab, item) {
            const result = await this.$http.post("api/systemSettings/reveal", { table: TABLE_KEYS[tab], key: item.key });
            this.revealDialog = { open: true, key: item.key, value: result.value || "(empty)" };
            this.loaded.history = false;
        },
        closeReveal() {
            this.revealDialog = { open: false, key: "", value: "" };
        },
        buildRolloverCandidates() {
            const from = this.rollover.from;
            if (!from) {
                this.rolloverCandidates = [];
                return;
            }
            const terms = this.terms
                .filter((t) => t.term === from)
                .map((t) => ({ uid: "t" + t.id, kind: "term", id: t.id, from, label: t.source, selected: true }));
            const settings = this.settings.settings
                .filter((s) => s.type === "regid" && (s.value || "").trim() === from)
                .map((s) => ({ uid: "s" + s.key, kind: "setting", key: s.key, from, label: s.key, selected: true }));
            this.rolloverCandidates = terms.concat(settings);
        },
        async applyRollover() {
            const to = this.rollover.to.trim();
            const count = this.rolloverSelected.length;
            if (!confirm(`Change ${count} item(s) from ${this.rollover.from} to ${to}? Views, web pages and phone lines switch immediately.`)) return;
            await this.applyTermChanges(this.rolloverSelected.map((c) => ({ kind: c.kind, id: c.id, key: c.key, from: c.from, to })));
            this.rollover = { from: null, to: "" };
        },
        termRule(value) {
            return this.validTerm(value) || "Use a term like RG2028";
        },
        openTermChange(items) {
            const list = (items || []).slice();
            if (!list.length) return;
            const same = list.every((item) => item.term === list[0].term);
            this.termDialog = { open: true, items: list, to: same ? list[0].term : "" };
        },
        termWillChange(item) {
            const to = (this.termDialog.to || "").trim();
            return this.validTerm(to) && item.term !== to;
        },
        async saveTermDialog() {
            const to = (this.termDialog.to || "").trim();
            const changes = this.termDialog.items
                .filter((item) => item.term !== to)
                .map((item) => ({ kind: "term", id: item.id, from: item.term, to }));
            if (!changes.length || !this.validTerm(to)) return;
            await this.applyTermChanges(changes);
            this.termDialog.open = false;
            this.termSelected = [];
        },
        async applyTermChanges(changes) {
            this.saving = true;
            try {
                const result = await this.$http.post("api/systemSettings/applyTermChanges", { changes });
                this.$notify({ group: "notification", type: "success", text: `${result.updated} updated` });
                await this.loadSettings("settings");
                await this.loadTerms();
                this.loaded.history = false;
            } finally {
                this.saving = false;
            }
        },
        async viewTable(item) {
            const rows = await this.$http.get("api/systemSettings/featureTableRows", { params: { name: item.name } });
            this.tableDialog = { open: true, name: item.name, columns: rows.length ? Object.keys(rows[0]) : [], rows };
        },
        async viewCommand(item) {
            const id = item.id != null ? item.id : item.iD;
            this.commandDialog = {
                open: true,
                loading: true,
                title: `${item.eventType} ${item.schemaName}.${item.objectName}`,
                command: "",
            };
            const result = await this.$http.get("api/systemSettings/schemaChangeCommand", { params: { id } });
            this.commandDialog.loading = false;
            this.commandDialog.command = ((result && (result.command || result.Command)) || "").trim();
        },
    },
};
</script>

<style scoped>
.pointer {
    cursor: pointer;
}
.mono {
    font-family: Consolas, "Courier New", monospace;
}
.pre-wrap {
    white-space: pre-wrap;
}
.break-all {
    word-break: break-all;
}
.value-cell {
    max-width: 380px;
    word-break: break-word;
}
.table-scroll {
    overflow: auto;
    max-height: 75vh;
}
.simple-table {
    border-collapse: collapse;
    font-size: 13px;
}
.simple-table th,
.simple-table td {
    border: 1px solid #ddd;
    padding: 4px 8px;
    text-align: left;
    vertical-align: top;
    max-width: 360px;
}
.simple-table th {
    background: #f5f5f5;
    position: sticky;
    top: 0;
}
.sql-box {
    white-space: pre-wrap;
    background: #f5f5f5;
    padding: 12px;
    max-height: 70vh;
    overflow: auto;
}
.docs-shell {
    display: flex;
    height: calc(100vh - 175px);
    min-height: 560px;
    border-top: 1px solid #e6e6e6;
}
.docs-nav {
    width: 240px;
    flex-shrink: 0;
    border-right: 1px solid #e6e6e6;
    background: #fafafa;
    overflow-y: auto;
    padding: 14px 10px;
}
.docs-nav-group {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #757575;
    padding: 4px 10px 6px;
}
.docs-nav-item {
    padding: 7px 10px;
    border-radius: 5px;
    font-size: 13.5px;
    line-height: 1.35;
    color: #333;
    cursor: pointer;
    margin-bottom: 2px;
}
.docs-nav-item:hover {
    background: #eeeeee;
}
.docs-nav-item--active {
    background: #e8f5e9;
    color: #2e7d32;
    font-weight: 600;
}
.docs-reader {
    flex: 1 1 auto;
    overflow-y: auto;
    padding: 28px 40px 48px;
    background: #fff;
}
.docs-chat {
    width: 380px;
    flex-shrink: 0;
    border-left: 1px solid #e6e6e6;
    background: #fafafa;
    display: flex;
    flex-direction: column;
}
.docs-chat-head {
    padding: 12px 16px;
    font-weight: 600;
    font-size: 14px;
    border-bottom: 1px solid #e6e6e6;
    background: #fff;
}
.docs-chat-sub {
    font-weight: 400;
    font-size: 12px;
    color: #757575;
}
.ask-when {
    margin-top: 4px;
    font-size: 11px;
    color: #9e9e9e;
}
.term-link {
    color: #2e7d32;
    font-weight: 600;
    text-decoration: none;
    border-bottom: 1px dashed #2e7d32;
}
.term-change-list {
    max-height: 280px;
    overflow-y: auto;
    border: 1px solid #e6e6e6;
    border-radius: 4px;
}
.term-change-row {
    padding: 10px 12px;
    border-bottom: 1px solid #eeeeee;
}
.term-change-row:last-child {
    border-bottom: 0;
}
.ask-log {
    flex: 1 1 auto;
    overflow-y: auto;
    padding: 14px;
}
.ask-empty {
    color: #616161;
    font-size: 13.5px;
    line-height: 1.5;
}
.ask-chip {
    background: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    padding: 8px 10px;
    margin-bottom: 6px;
    font-size: 13px;
    color: #2e7d32;
    cursor: pointer;
}
.ask-chip:hover {
    border-color: #4caf50;
    background: #f1f8e9;
}
.ask-turn {
    margin-bottom: 14px;
}
.ask-bubble {
    padding: 9px 12px;
    border-radius: 8px;
    margin-bottom: 6px;
    font-size: 13.5px;
    line-height: 1.5;
}
.ask-bubble--user {
    background: #e8f5e9;
    color: #1b5e20;
    font-weight: 500;
    white-space: pre-wrap;
}
.docs-chat-input >>> textarea {
    max-height: 140px;
    overflow-y: auto;
    margin-top: 0;
}
.ask-bubble--assistant {
    background: #fff;
    border: 1px solid #e4e4e4;
}
.docs-chat-input {
    border-top: 1px solid #e6e6e6;
    background: #fff;
    padding: 6px 8px;
}
.markdown-body {
    max-width: 780px;
    font-size: 15px;
    line-height: 1.7;
    color: #222;
}
.markdown-body >>> h1 {
    font-size: 26px;
    font-weight: 600;
    margin: 0 0 14px;
}
.markdown-body >>> h2 {
    font-size: 19px;
    font-weight: 600;
    margin: 28px 0 10px;
    border-bottom: 1px solid #ececec;
    padding-bottom: 5px;
}
.markdown-body >>> h3 {
    font-size: 16px;
    font-weight: 600;
    margin: 20px 0 6px;
}
.markdown-body >>> p {
    margin: 0 0 12px;
}
.markdown-body >>> code {
    background: #f1f3f4;
    padding: 2px 5px;
    border-radius: 4px;
    box-shadow: none;
    color: #1f2937;
    font-family: Consolas, "Courier New", monospace;
    font-size: 13px;
    font-weight: normal;
}
.markdown-body >>> pre {
    background: #f5f5f5;
    padding: 12px;
    border-radius: 6px;
    overflow: auto;
}
.markdown-body >>> pre code {
    background: none;
    color: inherit;
    padding: 0;
}
.markdown-body >>> table {
    width: 100%;
    border-collapse: collapse;
    margin: 10px 0 18px;
    font-size: 13.5px;
}
.markdown-body >>> th,
.markdown-body >>> td {
    border: 1px solid #e2e2e2;
    padding: 7px 11px;
    text-align: left;
    vertical-align: top;
}
.markdown-body >>> th {
    background: #fafafa;
    font-weight: 600;
}
.markdown-body >>> ul,
.markdown-body >>> ol {
    padding-left: 22px;
    margin-bottom: 12px;
}
.markdown-body >>> li {
    margin-bottom: 4px;
}
@media (max-width: 1264px) {
    .docs-shell {
        flex-direction: column;
        height: auto;
    }
    .docs-nav,
    .docs-chat {
        width: 100%;
        border: 0;
        border-bottom: 1px solid #e6e6e6;
    }
}
</style>
